<template>
  <layout-admin-dashboard-view
    :show-toolbar="false"
    :back-route="{ name: 'admin' }">
    <template #title>
      <span>{{ translateTitleCase("user.admin.list.displayTitle") }}</span>
    </template>

    <template #default>
      <div>
        <custom-crud-list
          v-bind="listConfiguration"
          @crud-list:items-updated="playground">
          <template #cell-createdAt="{ item }">
            <span class="ltr created-at-cell-content inline-block">
              {{ translateDate(item.createdAt, "dateWithTime") }}
            </span>
          </template>
          <template #cell-phoneNumber="{ item }">
            <span class="inline-block ltr text-inline-start">
              {{ tu(item.phoneNumber) }}
            </span>
          </template>
          <template #cell-userGroups="{ item }">
            <span class="flex gap-xs flex-wrap">
              <badge v-for="group in item.UserGroups">
                {{ tu(group.name) }}
              </badge>
            </span>
          </template>

          <template #cell-state="{ item }">
            <span v-if="item.state === 1"
              ><icon icon="check" theme="success"
            /></span>
            <span v-if="item.state === 0"><icon icon="x" theme="error" /></span>
            <span v-if="item.state !== 1 && item.state !== 0">-</span>
          </template>
        </custom-crud-list>
        <modal
          size="m"
          title="user.admin.list.actions.massEmail.modalTitle"
          :show="showMassEmailUi"
          @modal:closed="showMassEmailUi = false">
          <template #default>
            <p class="margin-l-bottom">
              {{ tu("user.admin.list.actions.massEmail.sendExplain") }}
            </p>

            <div class="form-wrapper margin-xl-bottom">
              <form-input
                class="margin-m-bottom"
                type="text"
                name="massSendSubject"
                v-model="massEmailData.subject"
                label="user.admin.list.actions.massEmail.emailTitle"></form-input>
              <form-input
                type="textarea"
                name="massSendSubject"
                v-model="massEmailData.content"
                label="user.admin.list.actions.massEmail.emailContent"></form-input>
            </div>

            <p class="text-l bold margin-l-bottom">
              {{ tu("user.admin.list.actions.massEmail.recipientListExplain") }}
              ({{ massSendUsers?.length }})
            </p>

            <table class="table table--s table--striped">
              <thead class="bold">
                <tr>
                  <td>#</td>
                  <td>
                    {{
                      tu("user.admin.list.actions.massEmail.tableUserFullName")
                    }}
                  </td>
                  <td>
                    {{ tu("user.admin.list.actions.massEmail.tableUserEmail") }}
                  </td>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(user, index) of massSendUsers">
                  <td>{{ +index + 1 }}</td>
                  <td>{{ user.fullName }}</td>
                  <td>{{ user.email }}</td>
                </tr>
              </tbody>
            </table>
          </template>

          <template #footer>
            <div class="flex flex-between flex-wrap gap-m width-expand">
              <form-button
                :loading="massSendLoading"
                :disabled="massSendLoading"
                @click="cancelMassEmail"
                theme="inverse"
                icon="x"
                text="user.admin.list.actions.massEmail.cancel"></form-button>
              <form-button
                :loading="massSendLoading"
                :disabled="massSendLoading"
                @click="sendMassEmail"
                theme="success"
                icon="check"
                text="user.admin.list.actions.massEmail.confirm"></form-button>
            </div>
          </template>
        </modal>
      </div>
    </template>
  </layout-admin-dashboard-view>
</template>

<script>
import asyncOperations from "@/client/extensions/composition/asyncOperations";
import CustomCrudList from "@/client/components/crud/customV1/List.vue";

export default {
  components: { CustomCrudList },
  layout: "adminDashboard",
  middleware: {
    requireSuperAdmin: {},
  },
  props: {},
  setup(props) {
    let { asyncOps, asyncOpsReady, asyncStatus } = asyncOperations(props);
    return { asyncOps, asyncOpsReady, asyncStatus };
  },
  meta: "user.admin.list.pageTitle",
  data: function () {
    return {
      order: false,
      userForm: false,
      massSendUsers: [],
      showMassEmailUi: false,
      massSendLoading: false,
      massEmailData: {
        subject: "",
        content: "",
        userIds: [],
      },
      listConfiguration: {
        subject: "user",
        listSrc: "entity/user",
        initialOrdering: { key: "createdAt", direction: "desc" },
        showIdField: true, // bool deafult true
        sortable: false, //bool, default false
        publishable: true, //bool, default false
        enableMassActions: true,
        editRoutePrefix: "admin-",
        editRoute: (item) => {
          this.$router.push({
            name: "admin-entity-entityType-edit-entityId",
            params: {
              entityType: "user",
              entityId: item.id,
            },
          });
        },
        createRoute: (item) => {
          this.$router.push({
            name: "admin-entity-entityType-create",
            params: {
              entityType: "user",
            },
          });
        },
        additionalListData: {
          simplified: false,
          translate: false,
        },
        fields: {
          // these are the table columns
          state: {
            itemKey: "state",
            label: "user.admin.list.fields.state",
            filterable: {
              name: "state",
              type: "select2",
              placeholder: "user.admin.list.fields.state",
              translateList: true,
              list: [
                { value: 0, label: "user.admin.list.statuses.unpublished" },
                { value: 1, label: "user.admin.list.statuses.published" },
              ],
            }, // boolean, or object
            quickEdit: {
              type: "binaryInteger",
            },
            orderable: true,
          },
          createdAt: {
            itemKey: "createdAt",
            label: "userAction.fields.createdAt",
          },
          createdFrom: {
            itemKey: "createdFrom",
            label: "userAction.fields.createdFrom",
            listable: false,
            filterable: {
              name: "createdFrom",
              type: "datePicker",
              placeholder: "user.admin.list.fields.createdFrom",
            },
            orderable: false,
          },
          createdBefore: {
            itemKey: "createdBefore",
            label: "userAction.fields.createdBefore",
            listable: false,
            filterable: {
              name: "createdBefore",
              type: "datePicker",
              placeholder: "user.admin.list.fields.createdBefore",
            },
            orderable: false,
          },
          fullName: {
            itemKey: "fullName",
            label: "user.admin.list.fields.fullName",
            orderable: true,
            filterable: true,
          },
          email: {
            itemKey: "email",
            label: "user.admin.list.fields.email",
            orderable: true,
            filterable: true,
          },
          phoneNumber: {
            itemKey: "phoneNumber",
            label: "user.admin.list.fields.phoneNumber",
            orderable: false,
            filterable: true,
          },
          userGroups: {
            itemKey: "userGroups",
            label: "user.admin.list.fields.userGroups",
            orderable: false,
            filterable: {
              type: "Select2",
              asyncListSource: "entity/userGroup",
              asyncDataTextFilterParamName: "title",
              textFilterAsSaffronListFilter: true,
              asyncListData: {
                simplified: true,
              },
            },
          },

          actionCreatedFrom: {
            itemKey: "actionCreatedFrom",
            label: "userAction.fields.createdFrom",
            listable: false,
            filterable: {
              name: "actionCreatedFrom",
              type: "datePicker",
              placeholder: "user.admin.list.fields.actionCreatedFrom",
            }, //
            orderable: true,
          },
          actionCreatedUntil: {
            itemKey: "actionCreatedUntil",
            label: "userAction.fields.createdUntil",
            listable: false,
            filterable: {
              name: "actionCreatedUntil",
              type: "datePicker",
              placeholder: "user.admin.list.fields.actionCreatedUntil",
            }, //
            orderable: true,
          },

          performedActionType: {
            itemKey: "actionType",
            label: "userAction.fields.actionType",
            listable: false,
            filterable: {
              name: "performedActionType",
              placeholder: "userAction.fieldFilters.performedActionType",
              type: "Select2",
              itemComponent: "iconItem",
              asyncListSource: "user-action/action/list",
              asyncListLive: false,
              textFilterAsSaffronListFilter: false,
              multiple: false,
              asyncListData: {
                simplified: true,
              },
            }, // boolean, or object
            orderable: true,
          },
          didNotPerformActionType: {
            itemKey: "actionType",
            label: "userAction.fields.actionType",
            listable: false,
            filterable: {
              name: "didNotPerformActionType",
              placeholder: "userAction.fieldFilters.didNotPerformActionType",
              type: "Select2",
              itemComponent: "iconItem",
              asyncListSource: "user-action/action/list",
              asyncListLive: false,
              textFilterAsSaffronListFilter: false,
              multiple: false,
              asyncListData: {
                simplified: true,
              },
            }, // boolean, or object
            orderable: true,
          },
        },
        actions: {
          show: false,
          edit: true,
          delete: false,
          custom: {
            activate: {
              //tooltip
              tooltip: { content: "user.admin.list.actions.activate.label" },
              icon: "shield-check",
              theme: "success",
              show: (item) => {
                return item.state !== 1;
              },
              //text
              handler: async ({ item }) => {
                if (!item || !item.id) {
                  this.$s.ui.notification(
                    "user.admin.list.actions.activate.errorNotification",
                    "error"
                  );
                  return false;
                }

                let res = await this.asyncOps.asyncCall(
                  `user/${item.id}/activate`,
                  {},
                  { method: "patch" }
                );

                if (res.hasError) {
                  this.$s.ui.notification("core.genericError", "error");
                  return false;
                }

                item.state = 1;
                this.$s.ui.notification(
                  "user.admin.list.actions.activate.successNotification",
                  "success"
                );
              },
            },
            ban: {
              //tooltip
              tooltip: { content: "user.admin.list.actions.ban.label" },
              icon: "shield-x",
              theme: "error",
              show: (item) => {
                return item.state !== 0;
              },
              handler: async ({ item }) => {
                if (!item || !item.id) {
                  this.$s.ui.notification(
                    "user.admin.list.actions.ban.errorNotification",
                    "error"
                  );
                  return false;
                }

                this.$s.ui.modal
                  .confirm("user.admin.list.actions.ban.confirmText")
                  .then(async () => {
                    let res = await this.asyncOps.asyncCall(
                      `user/${item.id}/ban`,
                      {},
                      { method: "patch" }
                    );

                    if (res.hasError) {
                      this.$s.ui.notification("core.genericError", "error");
                      return false;
                    }

                    item.state = 0;
                    this.$s.ui.notification(
                      "user.admin.list.actions.ban.successNotification",
                      "success"
                    );
                  })
                  .catch((e) => {
                    this.$s.ui.notification("core.actionCanceled", "info");
                  });
              },
            },
            loginAsUser: {
              //tooltip
              tooltip: { content: "user.admin.list.actions.loginAsUser.label" },
              icon: "arrow-login-inline-end",
              theme: "inverse",
              //text
              handler: async ({ item }) => {
                if (!item || !item.id) {
                  this.$s.ui.notification(
                    "user.admin.list.actions.loginAsUser.errorNotification",
                    "error"
                  );
                  return false;
                }

                let res = await this.asyncOps.asyncCall(
                  `token/refresh/user/${item.id}`,
                  {},
                  { method: "post" }
                );

                if (res.hasError) {
                  this.$s.ui.notification("core.genericError", "error");
                  return false;
                }

                this.$store.commit("user/persistCurrentUser");

                this.$store.commit("user/setToken", res.data.jwt);
                this.$store.commit(
                  "user/setRefreshToken",
                  res.data.refreshToken
                );

                this.$store.commit(
                  "user/setRefreshTokenExpires",
                  res.data.refreshTokenExpires
                );

                let loginResult = await this.$store.dispatch(
                  "user/updateUserProfile"
                );

                if (loginResult === false) {
                  this.$saffron.ui.notification(
                    this.safeTranslate(
                      "user.admin.list.actions.loginAsUser.loginFailErrorAndLostAccess"
                    ),
                    "danger"
                  );
                  this.$store.commit("user/invalidateAuth");
                  this.$store.commit("user/clearProfile");
                  this.$router.push("/");
                  return false;
                }

                this.$s.ui.notification(
                  "user.admin.list.actions.loginAsUser.successNotification",
                  "success"
                );


                this.$router.push("/");
              },
            },
          },
        },
        massActions: {
          delete: true,
          custom: {
            massEmail: {
              text: "user.admin.list.actions.massEmail.label",
              tooltip: "user.admin.list.actions.massEmail.tooltip",
              icon: "email",
              theme: "inverse",
              handler: ({ items }) => {
                console.log("mass send handler", items);
                this.resetMassSendUi();
                this.massSendUsers = items;
                this.showMassEmailUi = true;
                this.massEmailData.userIds = items.map((item) => item.id);
              },
            },
          },
        },
      },
      asyncData: {
        userForm: "entity/user/update",
      },
    };
  },
  computed: {
    buyerInformationList() {
      const res = [];
      //  console.log(this.userForm.fields);
      Object.entries(this.userForm.fields).forEach(([key, field]) => {
        let label;

        if (field.placeholder) {
          label = field.placeholder;
        }
        if (field.label) {
          label = field.label;
        }
        res.push({ label: label, value: this?.order?.Buyer[key] });
      });
      return res;
    },
  },
  methods: {
    resetMassSendUi() {
      this.massSendUsers = [];
      this.massSendLoading = false;
      this.showMassEmailUi = false;
      this.massEmailData.subject = "";
      this.massEmailData.content = "";
      this.massEmailData.userIds = [];
    },
    cancelMassEmail() {
      this.resetMassSendUi();
      this.$s.ui.notification(
        "user.admin.list.actions.massEmail.sendScheduledCancel"
      );
    },
    async sendMassEmail() {
      let url = "user/mass-email";
      let data = {
        userIds: this.massEmailData.userIds,
        subject: this.massEmailData.subject,
        content: this.massEmailData.content,
      };
      this.massSendLoading = true;

      let res = await this.asyncOps.post(url, data);
      this.massSendLoading = false;

      if (res.hasError) {
        this.$s.ui.notification(
          "user.admin.list.actions.massEmail.sendScheduledError",
          "error"
        );
      } else {
        this.resetMassSendUi();
        this.$s.ui.notification(
          "user.admin.list.actions.massEmail.sendScheduledSuccess",
          "success"
        );
      }
    },
  },
};
</script>
